import React, { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence, useScroll, useTransform, useInView, useSpring } from "framer-motion";
import { useNavigate } from "react-router-dom";
import "./FullAbt.css";

// Assets imported from Sollas About design system
import heroBg from "../../assets/SollasAbout/about_hero_bg.png";
import heroDecorate from "../../assets/SollasAbout/hero_decorate.svg";
import heroBlur from "../../assets/SollasAbout/hero_blur.png";
import counterExp from "../../assets/SollasAbout/counter_exp.svg";
import counterInstall from "../../assets/SollasAbout/counter_install.svg";
import counterSatisfaction from "../../assets/SollasAbout/counter_satisfaction.svg";
import valSolar from "../../assets/SollasAbout/val_solar.svg";
import valSupport from "../../assets/SollasAbout/val_support.svg";
import valService from "../../assets/SollasAbout/val_service.svg";
import proc1 from "../../assets/SollasAbout/proc_1.webp";
import proc2 from "../../assets/SollasAbout/proc_2.webp";
import proc3 from "../../assets/SollasAbout/proc_3.webp";
import procBlur from "../../assets/SollasAbout/proc_blur.svg";
import commitMission from "../../assets/SollasAbout/commit_mission.webp";
import commitVision from "../../assets/SollasAbout/commit_vision.webp";
import proj1 from "../../assets/SollasAbout/proj_1.webp";
import proj2 from "../../assets/SollasAbout/proj_2.webp";
import proj3 from "../../assets/SollasAbout/proj_3.webp";
import proj4 from "../../assets/Project/image4.png";
import projArrow from "../../assets/SollasAbout/proj_arrow.svg";
import HomeCTA from "../HomeCTA/HomeCTA";

// Number Counter matching Sollas Script 12
const AnimatedCounter = ({ target, suffix = "" }) => {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "0px 0px -15% 0px" });

  useEffect(() => {
    if (!inView) return;
    const isDecimal = target % 1 !== 0;
    const baseSpeed = 40;
    const duration = Math.min(2.5, Math.max(0.8, target / baseSpeed)) * 1000;
    const startTime = performance.now();

    const update = (now) => {
      const elapsed = now - startTime;
      const progress = Math.min(1, elapsed / duration);
      // power2.out / easeOutCubic: 1 - (1 - t)^3
      const eased = 1 - Math.pow(1 - progress, 3);
      const val = eased * target;
      setCount(isDecimal ? parseFloat(val.toFixed(1)) : Math.floor(val));

      if (progress < 1) {
        requestAnimationFrame(update);
      } else {
        setCount(target);
      }
    };

    const animId = requestAnimationFrame(update);
    return () => cancelAnimationFrame(animId);
  }, [inView, target]);

  return (
    <span ref={ref} className="about-counter-number">
      {count}{suffix}
    </span>
  );
};

const FullAbout = () => {
  const [activeFAQ, setActiveFAQ] = useState(null);
  const [isDesktop, setIsDesktop] = useState(window.innerWidth >= 992);
  const navigate = useNavigate();

  useEffect(() => {
    const handleResize = () => setIsDesktop(window.innerWidth >= 992);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Benefit / Values section scroll tracking (Exact Sollas 200vh pinned card movement)
  const benefitSectionRef = useRef(null);
  const { scrollYProgress: benefitScrollProgress } = useScroll({
    target: benefitSectionRef,
    offset: ["start start", "end end"],
  });
  const smoothBenefitProgress = useSpring(benefitScrollProgress, {
    stiffness: 100,
    damping: 24,
    mass: 0.2,
  });
  // Values cards gradual upward movement:
  // Card 1 stays at y: 0
  // Card 2 moves from 80px up to 0px across progress 0.0 to 0.45
  const card2Y = useTransform(smoothBenefitProgress, [0, 0.45], [80, 0]);
  // Card 3 moves from 160px up to 0px across progress 0.15 to 0.65 (staggered)
  const card3Y = useTransform(smoothBenefitProgress, [0, 0.15, 0.65], [160, 160, 0]);

  // Process timeline scroll progress tracking (Scrubbed fill t-ae40c097)
  const processWidgetsRef = useRef(null);
  const { scrollYProgress: processScrollProgress } = useScroll({
    target: processWidgetsRef,
    offset: ["start 65%", "end 75%"],
  });
  const smoothProcessProgress = useSpring(processScrollProgress, {
    stiffness: 90,
    damping: 24,
    mass: 0.2,
  });
  const processLineHeight = useTransform(smoothProcessProgress, [0, 1], ["0%", "100%"]);

  // Project section horizontal scroll tracking (Exact Sollas pinned horizontal track)
  const projectSectionRef = useRef(null);
  const { scrollYProgress: projectScrollProgress } = useScroll({
    target: projectSectionRef,
    offset: ["start start", "end end"],
  });
  const smoothProjectProgress = useSpring(projectScrollProgress, {
    stiffness: 120,
    damping: 24,
    mass: 0.1,
  });
  // Transform x from 0% to -74% across the pinned runway for 4 cards
  const horizontalX = useTransform(smoothProjectProgress, [0, 1], ["0%", "-74%"]);
  // Internal image parallax shifts linked to scroll position
  const imgParallax1 = useTransform(smoothProjectProgress, [0, 0.35], ["0%", "8%"]);
  const imgParallax2 = useTransform(smoothProjectProgress, [0.15, 0.6], ["-8%", "8%"]);
  const imgParallax3 = useTransform(smoothProjectProgress, [0.4, 0.85], ["-8%", "8%"]);
  const imgParallax4 = useTransform(smoothProjectProgress, [0.65, 1], ["-8%", "0%"]);

  // FAQ Accordion Data
  const faqData = [
    {
      q: "What does Dazbog do?",
      a: "Dazbog supports renewable energy projects from start to finish. We provide integrated advisory, technical due diligence, financial structuring, and execution oversight for utility-scale solar, hybrid, and battery storage projects.",
    },
    {
      q: "Do you work only as an advisor, or also during execution?",
      a: "We support the full project lifecycle. In addition to planning and financial advisory, we provide on-ground EPC coordination, quality supervision, and commissioning assurance.",
    },
    {
      q: "What types of renewable energy projects do you handle?",
      a: "Our core portfolio spans utility-scale solar power plants, solar + BESS (battery energy storage systems), wind-solar hybrid projects, and emerging clean fuels including green hydrogen.",
    },
    {
      q: "Who do you typically work with?",
      a: "We partner with leading clean energy developers, independent power producers (IPPs), infrastructure funds, banks, EPC contractors, and government utilities across India.",
    },
    {
      q: "At what stage can we engage Dazbog?",
      a: "You can engage us at any project stage—feasibility analysis, financial structuring, EPC tender evaluation, execution monitoring, or operational refinancing.",
    },
  ];

  // Exact Sollas Webflow IX2 animation timings and bezier curve
  // duration: 0.7s, ease: [0.16, 1, 0.3, 1]
  const slideUpVariants = {
    hidden: { opacity: 0, y: 45, scale: 0.98 },
    visible: (custom = 0) => ({
      opacity: 1,
      y: 0,
      scale: 1,
      transition: {
        duration: 0.75,
        delay: custom,
        ease: [0.16, 1, 0.3, 1],
      },
    }),
  };



  return (
    <div className="sollas-about-page">
      {/* ====================================================================
          1. ABOUT HERO SECTION
          Matches exact alignment, spacing, typography and reveal animations
          from Sollas About page (Webflow IX3 load-slide-up & t-be927768)
          ==================================================================== */}
      <section
        className="about-hero-section"
        style={{ backgroundImage: `url(${heroBg})` }}
      >
        <div className="sollas-container">
          <div className="about-layout">
            {/* Title & Lead with Staggered Entrance Reveal */}
            <motion.div
              className="about-hero-title-block"
              initial={{ opacity: 0, y: 80, scale: 0.92 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            >
              <h1 className="about-hero-title text-gradient">
                Building a Brighter, Solar-Powered Future
              </h1>
              <motion.p
                className="about-hero-paragraph"
                initial={{ opacity: 0, y: 45 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.75, delay: 0.15, ease: [0.16, 1, 0.3, 1] }}
              >
                We believe clean energy should be accessible to everyone. That’s why we create solar solutions that are efficient, affordable, and built to last.
              </motion.p>
              <motion.div
                className="about-hero-button-wrap"
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.75, delay: 0.28, ease: [0.16, 1, 0.3, 1] }}
              >
                <button
                  type="button"
                  className="button-primary"
                  onClick={() => navigate("/contact")}
                >
                  Get a Free Solar Quote
                </button>
              </motion.div>
            </motion.div>

            {/* Counter Block with 3 animated counters and vertical dividers */}
            <motion.div
              className="about-counter-main"
              initial={{ opacity: 0, x: 80, scale: 0.94 }}
              animate={{ opacity: 1, x: 0, scale: 1 }}
              transition={{ duration: 0.8, delay: 0.35, ease: [0.16, 1, 0.3, 1] }}
            >
              <div className="about-counter-block">
                {/* Stat 1 */}
                <div className="about-counter-widget">
                  <div className="project-counter-number-wrap">
                    <AnimatedCounter target={10} suffix="+" />
                  </div>
                  <div className="about-counter-footer">
                    <div className="about-counter-icon-wrap">
                      <img src={counterExp} alt="Experience" className="about-counter-icon" />
                    </div>
                    <div className="about-counter-footer-text">Years Experience</div>
                  </div>
                </div>

                <div className="divider-wrap about-counter-line" />

                {/* Stat 2 */}
                <div className="about-counter-widget">
                  <div className="project-counter-number-wrap">
                    <AnimatedCounter target={5000} suffix="+" />
                  </div>
                  <div className="about-counter-footer">
                    <div className="about-counter-icon-wrap">
                      <img src={counterInstall} alt="Installations" className="about-counter-icon" />
                    </div>
                    <div className="about-counter-footer-text">successful installations</div>
                  </div>
                </div>

                <div className="divider-wrap about-counter-line" />

                {/* Stat 3 */}
                <div className="about-counter-widget">
                  <div className="project-counter-number-wrap">
                    <AnimatedCounter target={98} suffix="%" />
                  </div>
                  <div className="about-counter-footer">
                    <div className="about-counter-icon-wrap">
                      <img src={counterSatisfaction} alt="Satisfaction" className="about-counter-icon" />
                    </div>
                    <div className="about-counter-footer-text">customer satisfaction</div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>

        {/* Ambient Decors & Exact Sollas Cut Shape */}
        <div className="white-linear" />
        <motion.img
          src={heroDecorate}
          alt=""
          className="hero-decoration-image-v2"
          initial={{ opacity: 0, y: "100%" }}
          animate={{ opacity: 1, y: "0%" }}
          transition={{ duration: 1.0, ease: [0.16, 1, 0.3, 1] }}
        />
        <motion.img
          src={heroBlur}
          alt=""
          className="about-hero-blur-image"
          initial={{ opacity: 0, scale: 0.85 }}
          animate={{ opacity: 0.75, scale: 1 }}
          transition={{ duration: 1.2, ease: [0.16, 1, 0.3, 1] }}
        />
      </section>

      {/* ====================================================================
          2. BENEFIT SECTION (Pinned 200vh & Scroll-Linked Cards)
          ==================================================================== */}
      <section ref={benefitSectionRef} className="benefit-section">
        <div className="benefit-vh">
          <div className="benefit-sticky">
            <div className="sollas-container">
              <div className="benefit-layout">
                <motion.div
                  className="benefit-title-block"
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true, amount: 0.2 }}
                  variants={slideUpVariants}
                >
                  <div className="benefit-title-wrap">
                    <h2 className="benefit-title text-gradient">
                      The Values That Shape Everything we Do
                    </h2>
                  </div>
                  <div className="benefit-button-wrap">
                    <button
                      type="button"
                      className="button-primary"
                      onClick={() => navigate("/services")}
                    >
                      Explore All service
                    </button>
                  </div>
                </motion.div>

                {/* 3 Value Cards with Scroll-Linked Gradual Upward Movement */}
                <div className="benefit-widgets-block">
                  {/* Card 1: Expertise You Can Trust */}
                  <div className="benefit-widget-v2 is-one">
                    <div className="benefit-widget-header">
                      <div className="benefit-widget-icons-wrap">
                        <img src={valSolar} alt="Solar System" className="benefit-widget-icon-v2 one" />
                        <img src={valSolar} alt="Solar System" className="benefit-widget-icon-v2 two" />
                      </div>
                    </div>
                    <div className="benefit-widget-footer-v2">
                      <h3 className="benefit-widget-title-v2">Expertise You Can Trust</h3>
                      <p className="benefit-widget-short-text">
                        Our team consists of certified engineers, designers, and installers with years of hands-on solar experience.
                      </p>
                    </div>
                  </div>

                  {/* Card 2: Customer-First Approach (Gradual Upward Scroll Scrub) */}
                  <motion.div
                    className="benefit-widget-v2 is-two"
                    style={{ y: isDesktop ? card2Y : 0 }}
                  >
                    <div className="benefit-widget-header">
                      <div className="benefit-widget-icons-wrap">
                        <img src={valSupport} alt="Customer Support" className="benefit-widget-icon-v2 one" />
                        <img src={valSupport} alt="Customer Support" className="benefit-widget-icon-v2 two" />
                      </div>
                    </div>
                    <div className="benefit-widget-footer-v2">
                      <h3 className="benefit-widget-title-v2">Customer-First Approach</h3>
                      <p className="benefit-widget-short-text">
                        We believe in honest guidance, clear pricing, and long-term support—no hidden costs, no confusion.
                      </p>
                    </div>
                  </motion.div>

                  {/* Card 3: End-to-End Service (Gradual Upward Scroll Scrub Staggered) */}
                  <motion.div
                    className="benefit-widget-v2 is-three"
                    style={{ y: isDesktop ? card3Y : 0 }}
                  >
                    <div className="benefit-widget-header">
                      <div className="benefit-widget-icons-wrap">
                        <img src={valService} alt="End to End" className="benefit-widget-icon-v2 one" />
                        <img src={valService} alt="End to End" className="benefit-widget-icon-v2 two" />
                      </div>
                    </div>
                    <div className="benefit-widget-footer-v2">
                      <h3 className="benefit-widget-title-v2">End-to-End Service</h3>
                      <p className="benefit-widget-short-text">
                        From planning and permits to installation and monitoring, we manage everything seamlessly.
                      </p>
                    </div>
                  </motion.div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ====================================================================
          3. PROCESS SECTION (With Interactive Scroll Timeline & Curtain Reveal)
          Exact replication of Sollas Webflow IX3 curtain reveal (t-65893ea8),
          scale-up/down, and scrubbed timeline line (t-ae40c097)
          ==================================================================== */}
      <section className="process-section">
        <div className="sollas-container">
          <motion.div
            className="process-title-block"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.2 }}
            variants={slideUpVariants}
          >
            <div className="sollas-badge">Process</div>
            <h2 className="process-title text-gradient">
              Unlock Solar Energy &amp; Start Saving Today
            </h2>
          </motion.div>

          <div ref={processWidgetsRef} className="process-widgets-block">
            {/* Connecting Timeline Line with Dynamic Scroll Fill */}
            <div className="process-widget-line-wrapper">
              <div className="process-widget-line-wrap">
                <motion.div
                  className="process-widget-line"
                  style={{ height: processLineHeight }}
                />
                <div className="process-circle-wrap">
                  <div className="process-circle" />
                  <div className="process-circle" />
                  <div className="process-circle" />
                </div>
              </div>
            </div>

            {/* Step 1 */}
            <div className="process-widget">
              <div className="process-widget-image-block">
                <motion.img
                  src={proc1}
                  alt="Free Consultation & Site Assessment"
                  className="process-widget-image"
                  initial={{ scale: 1.15 }}
                  whileInView={{ scale: 1 }}
                  viewport={{ once: true, amount: 0.2 }}
                  transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
                />
                <img src={procBlur} alt="" className="process-widget-blur" />
                <motion.div
                  className="scroll-into-view radius-xs"
                  initial={{ y: "0%" }}
                  whileInView={{ y: "101%" }}
                  viewport={{ once: true, amount: 0.2 }}
                  transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
                />
              </div>
              <motion.div
                className="process-widget-contents"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ duration: 0.65, delay: 0.1, ease: [0.16, 1, 0.3, 1] }}
              >
                <div className="process-widget-label">STEP (01)</div>
                <h3 className="process-widget-title">Free Consultation &amp; Site Assessment</h3>
                <p className="process-widget-paragraph">
                  We evaluate your energy needs, roof space, and sunlight exposure to design the most efficient solar solution for your property.
                </p>
              </motion.div>
            </div>

            {/* Step 2 (Reversed) */}
            <div className="process-widget reverce">
              <div className="process-widget-image-block step-02">
                <motion.img
                  src={proc2}
                  alt="Custom System Design & Approval"
                  className="process-widget-image"
                  initial={{ scale: 1.15 }}
                  whileInView={{ scale: 1 }}
                  viewport={{ once: true, amount: 0.2 }}
                  transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
                />
                <img src={procBlur} alt="" className="process-widget-blur" />
                <motion.div
                  className="scroll-into-view radius-xs"
                  initial={{ y: "0%" }}
                  whileInView={{ y: "101%" }}
                  viewport={{ once: true, amount: 0.2 }}
                  transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
                />
              </div>
              <motion.div
                className="process-widget-contents"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ duration: 0.65, delay: 0.1, ease: [0.16, 1, 0.3, 1] }}
              >
                <div className="process-widget-label">STEP (02)</div>
                <h3 className="process-widget-title">Custom System Design &amp; Approval</h3>
                <p className="process-widget-paragraph">
                  Our experts create a tailored solar system plan and handle approvals, timelines, and cost clarity before installation begins.
                </p>
              </motion.div>
            </div>

            {/* Step 3 */}
            <div className="process-widget">
              <div className="process-widget-image-block">
                <motion.img
                  src={proc3}
                  alt="Professional Installation & Activation"
                  className="process-widget-image"
                  initial={{ scale: 1.15 }}
                  whileInView={{ scale: 1 }}
                  viewport={{ once: true, amount: 0.2 }}
                  transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
                />
                <img src={procBlur} alt="" className="process-widget-blur" />
                <motion.div
                  className="scroll-into-view radius-xs"
                  initial={{ y: "0%" }}
                  whileInView={{ y: "101%" }}
                  viewport={{ once: true, amount: 0.2 }}
                  transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
                />
              </div>
              <motion.div
                className="process-widget-contents"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ duration: 0.65, delay: 0.1, ease: [0.16, 1, 0.3, 1] }}
              >
                <div className="process-widget-label">STEP (03)</div>
                <h3 className="process-widget-title">Professional Installation &amp; Activation</h3>
                <p className="process-widget-paragraph">
                  Certified technicians install your system safely and efficiently, followed by testing and activation for immediate energy savings.
                </p>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* ====================================================================
          4. COMMITMENT SECTION (Our Mission & Our Vision)
          ANIMATIONS REMOVED AS REQUIRED BY USER (Constraint 5)
          Statically rendered, visually stable, no scroll effects or motion
          ==================================================================== */}
      <section className="commitment-section">
        <div className="sollas-container">
          <div className="commitment-title-block">
            <div className="sollas-badge">Commitment</div>
            <h2 className="commitment-title text-gradient">
              Committed to a Cleaner, Solar-Powered Future
            </h2>
          </div>

          <div className="commitment-widgets">
            {/* Mission - Animated Smoke Light Effect */}
            <motion.div
              className="commitment-widget mission"
              initial={{ opacity: 0, scale: 0.96, y: 30 }}
              whileInView={{ opacity: 1, scale: 1, y: 0 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 0.75, ease: [0.16, 1, 0.3, 1] }}
            >
              <img src={commitMission} alt="Our Mission" className="commitment-widget-image" />
              <div className="commitment-smoke-overlay mission" />
              <div className="commitment-widget-linear mission" />
              <div className="commitment-widget-contents">
                <h3 className="commitment-widget-title">Our Mission</h3>
                <p className="commitment-widget-paragraph">
                  Our mission is to make clean, reliable, and affordable solar energy accessible to homes and businesses. We deliver smart solar solutions that reduce energy costs, lower carbon emissions.
                </p>
              </div>
            </motion.div>

            {/* Vision - Animated Smoke Light Effect */}
            <motion.div
              className="commitment-widget vision"
              initial={{ opacity: 0, scale: 0.96, y: 30 }}
              whileInView={{ opacity: 1, scale: 1, y: 0 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 0.75, delay: 0.12, ease: [0.16, 1, 0.3, 1] }}
            >
              <img src={commitVision} alt="Our Vision" className="commitment-widget-image" />
              <div className="commitment-smoke-overlay vision" />
              <div className="commitment-widget-linear vision" />
              <div className="commitment-widget-contents">
                <h3 className="commitment-widget-title">Our Vision</h3>
                <p className="commitment-widget-paragraph">
                  Our vision is to lead the transition toward a sustainable energy future where solar power becomes the primary source of clean, renewable electricity for communities worldwide.
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ====================================================================
          5. PROJECT SECTION V2 (Pinned Horizontal Scroll Showcase)
          Replicates [hz-section] & [hz-scroll] from Sollas
          NOTE: Team Members section is REMOVED as per Constraint 4
          ==================================================================== */}
      {/* ====================================================================
          5. PROJECT SECTION V2 (Pinned Horizontal Scroll Showcase)
          Exact Sollas reference layout matching https://sollas.webflow.io/about
          Background: #cae6ff, container max-width: 1312px, 300vh horizontal scrub
          ==================================================================== */}
      <section ref={projectSectionRef} className="project-section-v2">
        <div className="project-container">
          <motion.div
            className="project-title-block-v2"
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.1 }}
            transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
          >
            <div className="project-title-wrapper">
              <div className="project-badge">Our Projects</div>
              <h2 className="project-title-v2">
                <span className="project-title-green">Our Solar Projects</span>
                <br />
                <span className="project-title-dark">Powering Real Results</span>
              </h2>
            </div>
            <div className="project-title-short-text-wrap">
              <p className="project-title-short-text">
                Explore our real solar installations, carefully designed, professionally installed, and expertly maintained to provide clean, reliable energy while delivering long-term savings
              </p>
            </div>
          </motion.div>

          <div className="project-widget-wrapper-block">
            <div className="project-widget-scroll">
              {/* Horizontal Track pinned during scroll on desktop */}
              <motion.div
                className="project-widgets-block"
                style={{ x: isDesktop ? horizontalX : "0%" }}
              >
                {/* Project 1 */}
                <div
                  className="project-widget-card"
                  style={{ cursor: "default" }}
                >
                  <div className="hz-item-image-wrapper">
                    <motion.img
                      src={proj1}
                      alt="Residential Rooftop Solar"
                      className="hz-item-image"
                      style={{ x: isDesktop ? imgParallax1 : 0 }}
                    />
                  </div>
                  <div className="project-contents">
                    <div className="project-widget-v2">
                      <p className="project-short-text text">
                        A customized rooftop solar system designed to maximize energy efficiency for a residential property. The system helps reduce electricity bills.
                      </p>
                      <div className="arrow-icons-wrapper">
                        <img src={projArrow} alt="Arrow" className="arrow-icon" />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Project 2 */}
                <div
                  className="project-widget-card"
                  style={{ cursor: "default" }}
                >
                  <div className="hz-item-image-wrapper">
                    <motion.img
                      src={proj2}
                      alt="Commercial Solar Power"
                      className="hz-item-image"
                      style={{ x: isDesktop ? imgParallax2 : 0 }}
                    />
                  </div>
                  <div className="project-contents">
                    <div className="project-widget-v2">
                      <p className="project-short-text text">
                        A large-scale commercial solar installation designed to support modern business operations with reliable, cost-effective renewable energy.
                      </p>
                      <div className="arrow-icons-wrapper">
                        <img src={projArrow} alt="Arrow" className="arrow-icon" />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Project 3 */}
                <div
                  className="project-widget-card"
                  style={{ cursor: "default" }}
                >
                  <div className="hz-item-image-wrapper">
                    <motion.img
                      src={proj3}
                      alt="Solar Water Pumps for Agriculture"
                      className="hz-item-image"
                      style={{ x: isDesktop ? imgParallax3 : 0 }}
                    />
                  </div>
                  <div className="project-contents">
                    <div className="project-widget-v2">
                      <p className="project-short-text text">
                        Power irrigation and water supply with efficient, eco-friendly solar water pumping solutions for farms, rural homes, and remote areas.
                      </p>
                      <div className="arrow-icons-wrapper">
                        <img src={projArrow} alt="Arrow" className="arrow-icon" />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Project 4 */}
                <div
                  className="project-widget-card"
                  style={{ cursor: "default" }}
                >
                  <div className="hz-item-image-wrapper">
                    <motion.img
                      src={proj4}
                      alt="Utility-Scale Hybrid Solar & Storage"
                      className="hz-item-image"
                      style={{ x: isDesktop ? imgParallax4 : 0 }}
                    />
                  </div>
                  <div className="project-contents">
                    <div className="project-widget-v2">
                      <p className="project-short-text text">
                        Utility-scale hybrid solar and battery storage solutions delivering stable, continuous renewable power for industrial and community grids.
                      </p>
                      <div className="arrow-icons-wrapper">
                        <img src={projArrow} alt="Arrow" className="arrow-icon" />
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* ====================================================================
          6. HARMONIZED FAQ & CTA SECTION
          ==================================================================== */}
      <section className="about-faq-section">
        <div className="sollas-container">
          <div className="row gy-5">
            <div className="col-lg-5">
              <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.2 }}
                variants={slideUpVariants}
              >
                <div className="sollas-badge">FAQ</div>
                <h2 className="faq-title text-gradient">
                  Know More About Dazbog Advisory
                </h2>
                <p className="faq-subtitle">
                  Clear answers to common questions about our role, advisory capabilities, and project execution support.
                </p>
              </motion.div>
            </div>
            <div className="col-lg-7">
              <div className="faq-accordion-list">
                {faqData.map((faq, i) => (
                  <motion.div
                    key={i}
                    className={`sollas-faq-item ${activeFAQ === i ? "active" : ""}`}
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true, amount: 0.2 }}
                    variants={slideUpVariants}
                    custom={i * 0.08}
                  >
                    <div
                      className="sollas-faq-question"
                      onClick={() => setActiveFAQ(activeFAQ === i ? null : i)}
                    >
                      <span>{faq.q}</span>
                      <div className="sollas-faq-toggle">
                        {activeFAQ === i ? "−" : "+"}
                      </div>
                    </div>
                    <AnimatePresence>
                      {activeFAQ === i && (
                        <motion.div
                          className="sollas-faq-answer"
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: "auto", opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
                        >
                          <p>{faq.a}</p>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </motion.div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>
      <HomeCTA />

    </div>
  );
};

export default FullAbout;
